/**
 * WhiteLabelEditorPage — full-page wrapper around WhiteLabelEditor.
 *
 * Mounted at /super-admin/tenants/:tenantId/brand. Reached from the Super
 * Admin → Edit Tenant modal. The page header shows the tenant name in the
 * same breadcrumb style as the Design System's inline editing header so the
 * user always knows which tenant they're editing — not a raw UUID.
 */

import { useEffect, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Sidebar } from '@/components/layout/Sidebar';
import api from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import WhiteLabelEditor from './WhiteLabelEditor';
import './components/component-demo.css';

export default function WhiteLabelEditorPage() {
  const { t } = useLanguage();
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const { tenantId: ownTenantId } = useAuth();
  const [tenantName, setTenantName] = useState<string>('');
  const [tenantCode, setTenantCode] = useState<string>('');

  // Fetch the tenant via the list endpoint — there's no GET /tenants/<id>
  // route, only PUT. The list is paginated to 1000 which covers every
  // realistic deployment.
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/super-admin/tenants', { params: { per_page: 1000 } });
        if (cancelled) return;
        const tenants = (res.data?.tenants ?? []) as any[];
        const t = tenants.find(x => x.tenant_id === tenantId);
        setTenantName(t?.tenant_name ?? '');
        setTenantCode(t?.tenant_code ?? '');
      } catch {
        // Silently fall through — the breadcrumb just shows the tenant id
        // if the lookup fails, the editor below surfaces its own error.
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  if (!tenantId) {
    return <div style={{ padding: 'var(--space-6)' }}>{t('whiteLabel.noTenantIdInUrl')}</div>;
  }

  const isOwn = !!ownTenantId && ownTenantId === tenantId;

  return (
    <div className="app-shell">
      <Sidebar />
      <div className="main-container">
        {/* Override global `.main-content { overflow: hidden }` from AdminPage.css —
            this page is taller than the viewport (form sections stack vertically)
            and needs the browser's native scroll. */}
        <main className="main-content" style={{ overflow: 'auto' }}>
          {/* Top-left back button — same recipe as every other back
              button across the Design System pages. */}
          <Button
            type="text"
            icon={<ArrowLeftOutlined className="rtl-flip" />}
            onClick={() => navigate(-1)}
            className="ds-back-button"
            data-testid="wl-page-back"
          >
            {t('common.back')}
          </Button>
          <div className="simple-chat-header">
            <h1 dir="auto" className="bot-title">{t('whiteLabel.pageTitleEditor')}</h1>
          </div>
          {/* Tenant identity breadcrumb — same compact pill the Design
              System inline editor uses. Tells the user which tenant they
              landed on without making them parse a UUID. */}
          <div className="ds-editing-header" data-testid="editing-header">
            {/* Separated by literal whitespace + the styled ` · ` code
                separator, mirroring DesignSystemPanel's subtitle. The
                previous markup leaned on `ds-editing-header-*` classes that
                were never added to white-label-editor.css, so the spans
                collapsed together and rendered "Editing brand forM-2m-2". */}
            {t('designSystem.designingBrandFor')} <strong dir="auto">{tenantName || '…'}</strong>
            {tenantCode && (
              <span className="ds-editing-subtitle-code"> · {tenantCode}</span>
            )}
            {isOwn && <> <Tag color="blue">{t('superAdmin.yourTenant')}</Tag></>}
          </div>
          <div className="wl-editor-host wl-page-host">
            <WhiteLabelEditor tenantId={tenantId} />
          </div>
        </main>
      </div>
    </div>
  );
}
